import '@ghq-abi/design-system-v2/css';

import * as React from 'react';
import NextNProgress from 'nextjs-progressbar';
import { AuthClientProvider } from '@ghq-abi/auth-client-lib/client';
import { DesignSystemProvider, theme } from '@ghq-abi/design-system';
import { ThemeProvider } from '@ghq-abi/design-system-v2';
import {
  Hydrate,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query';
import { TolgeeInstance, TolgeeProvider, useTolgeeSSR } from '@tolgee/react';

import { AuthGuard, ErrorBoundary } from '~/core/components';
import { AbilityProvider } from '~/core/contexts/AbilityContext';
import { FlagsProvider } from '~/core/contexts/FlagsContext';
import { NavProvider } from '~/core/contexts/NavContext';
import { SessionInfoProvider } from '~/core/contexts/SessionInfoContext';
import { ToastProvider } from '~/core/contexts/ToastContext';
import { AppTemplate } from '~/core/templates/App';
import { CustomAppProps } from '~/core/types';
import { createUserAbility } from '~/shared/auth/permissions';
import { queryClientConfig, useDatadog } from '~/shared/lib';
import { tolgee } from '~/shared/lib/tolgee';

import './globals.css';

export default function App({
  Component,
  pageProps: { session, abilityRules, flags, ...pageProps }
}: CustomAppProps) {
  // https://tanstack.com/query/v4/docs/react/guides/ssr#using-hydration
  const [queryClient] = React.useState(
    () => new QueryClient(queryClientConfig)
  );

  const locale = session?.user?.preferredLanguage || 'pt-BR';
  const ssrTolgee = useTolgeeSSR(tolgee as unknown as TolgeeInstance, locale);
  useDatadog(locale, session);

  return (
    <TolgeeProvider tolgee={ssrTolgee}>
      <ErrorBoundary>
        <AuthClientProvider session={session}>
          <SessionInfoProvider sessionInfo={session}>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider>
                <DesignSystemProvider>
                  <AuthGuard>
                    {Component.unauth ? (
                      <Component {...pageProps} />
                    ) : (
                      <ToastProvider>
                        <AbilityProvider
                          ability={createUserAbility(
                            session?.user,
                            abilityRules
                          )}>
                          <FlagsProvider flags={flags}>
                            <NavProvider>
                              <Hydrate state={pageProps.dehydratedState}>
                                <AppTemplate>
                                  <Component {...pageProps} />
                                </AppTemplate>
                              </Hydrate>
                              <NextNProgress
                                color={theme.colors.black.toString()}
                                startPosition={0.3}
                                stopDelayMs={200}
                                height={3}
                              />
                            </NavProvider>
                          </FlagsProvider>
                        </AbilityProvider>
                      </ToastProvider>
                    )}
                  </AuthGuard>
                </DesignSystemProvider>
              </ThemeProvider>
            </QueryClientProvider>
          </SessionInfoProvider>
        </AuthClientProvider>
      </ErrorBoundary>
    </TolgeeProvider>
  );
}
